/**
 * The catalogue pages' copy. The wording is the Django content pages' — the
 * shared table they and these are written from, and the existing dashboard
 * strings (templates/questionnaire_definition/*_dashboard.html) — so the two
 * apps read the same in each language.
 */
export const catalogueFr = {
  title: "Contenu du questionnaire",
  eyebrow: "Administration",
  loading: "Chargement…",
  error: "Une erreur est survenue.",
  tabs: {
    thematics: "Thématiques",
    issues: "Enjeux",
    goals: "Objectifs",
    actions: "Pistes d'action",
    sectors: "Secteurs",
    characteristics: "Caractéristiques",
  },
  sectorCategories: "Catégories de secteur",
  characteristicGroups: "Caractéristiques principales",
  add: "Ajouter",
  edit: "Modifier",
  delete: "Supprimer",
  cancel: "Annuler",
  save: "Enregistrer",
  close: "Fermer",
  moveUp: "Monter",
  moveDown: "Descendre",
  history: "Historique",
  historyTitle: "Historique des modifications",
  recent: "Historique récent",
  recentTitle: "Modifications récentes",
  missingTranslation: "Traduction manquante",
  noCategory: "Sans catégorie",
  noGroup: "Sans caractéristique principale",
  searchPlaceholder: "Rechercher…",
  empty: "Aucun élément.",
  noResults: "Aucun résultat",
  addTitle: {
    thematic: "Ajouter une thématique",
    issue: "Ajouter un enjeu",
    goal: "Ajouter un objectif",
    action: "Ajouter une piste d'action",
    sector: "Ajouter un secteur",
    sector_category: "Ajouter une catégorie de secteur",
    characteristic: "Ajouter une caractéristique",
    characteristic_group: "Ajouter une caractéristique principale",
  },
  editTitle: "Modifier « {{name}} »",
  help: {
    experts_enabled:
      "Autoriser les experts à se rattacher à cette thématique. Si décoché, la thématique (et ses enjeux et pistes d'action) n'est pas proposée à la sélection lors de l'inscription des experts, et aucun expert n'est affiché pour ses pistes d'action. Les sélections déjà faites par les experts sont conservées et réapparaissent si la case est recochée.",
    tools_enabled:
      "Autoriser l'association d'outils aux pistes d'action de cette thématique. Si décoché, aucun outil ne peut être associé à ses pistes d'action ni affiché pour celles-ci.",
    financial_aids_enabled:
      "Autoriser l'association d'aides financières aux pistes d'action de cette thématique. Si décoché, aucune aide ne peut être associée à ses pistes d'action ni affichée pour celles-ci.",
  },
  fields: {
    name_fr: "Texte (français)",
    name_en: "Texte (anglais)",
    thematic: "Thématique",
    issue: "Enjeu",
    goal: "Objectif",
    category: "Catégorie",
    group: "Caractéristique principale",
    prerequisite: "Prérequis",
    css_class: "Couleur",
    icon: "Icône",
    colour: "Couleur personnalisée",
    experts_enabled: "Experts autorisés",
    tools_enabled: "Outils autorisés",
    financial_aids_enabled: "Aides financières autorisées",
    sectors: "Secteurs",
    characteristics: "Caractéristiques",
    company_sizes: "Tailles d'entreprise",
    unavoidable_issues: "Enjeux incontournables",
    triggering_characteristics: "Caractéristiques déclencheuses",
    offered_characteristics: "Caractéristiques offertes",
    actions: "Pistes d'action filtrées",
    issues: "Enjeux déclenchés",
  },
  restore: {
    button: "Restaurer",
    title: "Restaurer « {{name}} »",
    required: "Le parent requis n’existe plus : {{field}} « {{name}} ». Choisissez-en un autre ci-dessous, ou annulez pour le restaurer d’abord.",
    requiredUnnamed: "Le parent requis n’existe plus : {{field}}. Choisissez-en un autre ci-dessous, ou annulez pour le restaurer d’abord.",
    choose: "Choisir…",
    warnings: "Un ou plusieurs éléments liés n’existent plus et ne seront pas restaurés :",
    continue: "Voulez-vous tout de même continuer ?",
    alreadyRestored: "Cet élément a déjà été restauré.",
    error: "La restauration a échoué. Réessayez.",
    done: "« {{name}} » a été restauré.",
  },
  icon: {
    hint: "Classe Font Awesome 5 de l'icône, par exemple « fas fa-leaf » : choisissez-la dans la liste ou copiez-la depuis la galerie d'icônes gratuites de Font Awesome.",
    gallery: "Galerie d'icônes Font Awesome",
    choose: "Choisir une icône",
    loading: "Chargement des icônes…",
    empty: "Aucune icône ne correspond à cette recherche.",
    search: "Rechercher une icône",
    searchHint: "La recherche porte sur les noms anglais de <gallery>Font Awesome</gallery>, par exemple « leaf » ou « recycle ».",
    unavailable: "La liste des icônes n'est pas disponible. Choisissez une icône dans la galerie Font Awesome, puis saisissez sa classe dans le champ.",
  },
  none: "Aucun",
  emptyMeansAll: "Laisser vide pour toutes les entreprises.",
  colours: {
    governance: "Gouvernance",
    economic: "Économique",
    social: "Sociale",
    environment: "Environnement",
    custom: "Personnalisée",
  },
  colour: {
    hint: "Code hexadécimal, par exemple #C2410C.",
    preview: "Aperçu",
    fill: "Bandeau",
    ink: "Texte",
    soft: "Fond pâle",
    unreadable:
      "Ni le texte foncé ni le blanc ne sont lisibles sur cette couleur : choisissez-la un peu plus claire ou plus foncée.",
  },
  impact: {
    title: "Confirmer la modification",
    intro: "Cette modification touche des entreprises existantes :",
    confirm: "Confirmer et enregistrer",
    stale: "Les chiffres ont changé depuis l'aperçu. Vérifiez-les à nouveau.",
  },
  deletion: {
    title: "Supprimer « {{name}} » ?",
    permanent: "Cette suppression est définitive.",
    blocked: "Suppression impossible : des données en dépendent.",
    blockedHelp: "Retirez ces dépendances avant de supprimer l'élément.",
  },
  saved: "« {{name}} » enregistré.",
  deleted: "« {{name}} » supprimé.",
  dashboard: {
    subtitle: {
      actions:
        "Corriger le texte des actions (français / anglais) et gérer l'accès aux experts.",
      goals:
        "Corriger le texte des objectifs (français / anglais) et désigner les objectifs présentés aux comptes démo.",
      issues: "Corriger le texte des enjeux (français / anglais).",
    },
    search: {
      actions: "Rechercher une action…",
      goals: "Rechercher un objectif…",
      issues: "Rechercher un enjeu…",
    },
    filters: {
      thematic: "Thématique",
      issue: "Enjeu",
      goal: "Objectif",
      english: "Traduction anglaise",
      allFeminine: "Toutes",
      allMasculine: "Tous",
      missing: "Manquante",
      present: "Présente",
      reset: "Réinitialiser",
    },
    count: {
      actions_one: "{{count}} piste d’action",
      actions_other: "{{count}} pistes d’action",
      actionsFiltered_one: "{{count}} piste d’action sur {{total}}",
      actionsFiltered_other: "{{count}} pistes d’action sur {{total}}",
      goals_one: "{{count}} objectif",
      goals_other: "{{count}} objectifs",
      goalsFiltered_one: "{{count}} objectif sur {{total}}",
      goalsFiltered_other: "{{count}} objectifs sur {{total}}",
      issues_one: "{{count}} enjeu",
      issues_other: "{{count}} enjeux",
      issuesFiltered_one: "{{count}} enjeu sur {{total}}",
      issuesFiltered_other: "{{count}} enjeux sur {{total}}",
    },
    bannerStrong: "L'enregistrement corrige le texte partout.",
    banner: {
      actions:
        "Modifier une action met à jour son libellé dans les plans d'action existants (sauf ceux renommés par l'entreprise) et dans les pages de communication déjà publiées qui l'utilisent.",
      goals:
        "Modifier un objectif met à jour son libellé dans les plans d'action existants (sauf ceux renommés par l'entreprise) et dans les pages de communication déjà publiées qui l'utilisent.",
      issues:
        "Modifier un enjeu met à jour son libellé dans les plans d'action existants et dans les pages de communication déjà publiées qui l'utilisent.",
    },
    text: {
      actions: "Texte de l'action",
      goals: "Texte de l'objectif",
      issues: "Texte de l'enjeu",
    },
    textEn: "Texte (anglais)",
    canHaveExperts: "Peut avoir des experts",
    availableInDemo: "Disponible en démonstration",
    pending: "En attente",
    pendingTitle: "Changement en attente de validation par une autre personne",
    on: "activé",
    off: "désactivé",
    modified: "Modifié",
    plans: "plans",
    communications: "communications",
    experts: "experts",
    tools: "Outils",
    aids: "Aides",
    toolOrderTitle: "Ordre d’affichage des outils",
    aidOrderTitle: "Ordre d’affichage des aides financières",
    noMatch: {
      actions: "Aucune action ne correspond à votre recherche.",
      goals: "Aucun objectif ne correspond à votre recherche.",
      issues: "Aucun enjeu ne correspond à votre recherche.",
    },
    dirty: {
      actions: "action(s) modifiée(s)",
      goals: "objectif(s) modifié(s)",
      issues: "enjeu(x) modifié(s)",
    },
    savedCount: {
      actions: "{{n}} action(s) enregistrée(s).",
      goals: "{{n}} objectif(s) enregistré(s).",
      issues: "{{n}} enjeu(x) enregistré(s).",
    },
    stagedCount: {
      actions: "{{n}} changement(s) d'accès expert en attente de validation.",
      goals: "{{n}} changement(s) de disponibilité en démonstration en attente de validation.",
    },
    saveChanges: "Enregistrer les modifications",
  },
  review: {
    approved: {
      "expert-access": "{{n}} changement(s) d'accès expert approuvé(s).",
      demo: "{{n}} changement(s) de disponibilité en démonstration approuvé(s).",
    },
    nothingToApprove: "Aucun changement à approuver.",
    countered: "{{n}} contre-proposition(s) enregistrée(s), en attente d'une autre personne.",
    nothingToReview: "Aucun changement à réviser.",
    noteRequired: "Une note est requise pour une contre-proposition.",
    validate: "À valider",
    validatePending: "Changements en attente de votre validation",
    validateNone: "Aucune validation en attente",
    title: {
      "expert-access": "Accès expert · à valider",
      demo: "Disponibilité en démonstration · à valider",
    },
    introReview: "Proposés par d'autres personnes.",
    introReviewStrong: "Vous ne pouvez pas valider vos propres propositions.",
    introPropose: {
      "expert-access":
        "Choisissez la valeur souhaitée pour chaque piste, puis justifiez. Votre proposition devra à son tour être validée.",
      demo: "Choisissez la valeur souhaitée pour chaque objectif, puis justifiez. Votre proposition devra à son tour être validée.",
    },
    proposedBy: "Proposé par",
    deletedUser: "Utilisateur supprimé",
    proposedValue: "Valeur proposée",
    enabled: "Activé",
    disabled: "Désactivé",
    note: "Note",
    required: "requise",
    notePlaceholder: "Justifiez votre contre-proposition…",
    proposals: "proposition(s)",
    later: "Plus tard",
    propose: "Proposer des changements",
    submitCounter: "Soumettre la contre-proposition",
    approveAll: "Approuver tout",
  },
};

export type CatalogueStrings = typeof catalogueFr;
